/**
 * @fileoverview Mock backend for the Sales CRM prospect endpoints and the prospect metadata.
 * @description Registers GET /tenants/prospect-metadata, GET /sales/prospects, GET
 * /sales/prospects/summary, GET /sales/prospects/eligible-owners, GET /sales/prospects/:id, POST
 * /sales/prospects, POST /sales/prospects/:id/withdraw and POST /sales/prospects/:id/assign-owner
 * on the feature's shared mock adapter,
 * answering in the backend's envelopes with its error codes (Confluence 3.5.4.1 and 3.5.11). Reads
 * and writes the shared Tenant store through sales-crm-mock-projection.ts. Follows the backend's
 * rules: the backlog is the caller's cohort of accepted, unconverted prospects (and, as the
 * frontend's backlog always was, none with an open target); a Sales Executive's new prospect waits
 * for review with the maker as its proposed owner, anyone else's is accepted at once, owned by the
 * Executive they named. Like the other sales mocks it doesn't check MFA freshness. A no-op when
 * VITE_ENABLE_MOCK_API is false.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { prospectApproverFor } from '@/features/sales/lib/prospect-approval.utils'
import { resolveDownlineUserIds } from '@/features/sales/lib/sales-hierarchy.utils'
import { listSalesRoster } from '@/features/sales/mocks/sales-roster-mock-store'
import { listTargets } from '@/features/sales/mocks/targets-mock-store'

import {
  admissionStateOf,
  catalogueNameById,
  isInCallerCohort,
  mockCaller,
  mockProspectMetadata,
  ownerOf,
  prospectEtagOf,
  recordDecision,
  reviewerOf,
  toFrontendRating,
  toProspectDetails,
  toProspectListItem,
} from './sales-crm-mock-projection'
import {
  bodyOf,
  failReply,
  headerOf,
  okEnvelope,
  pageReply,
  queryOf,
} from './sales-crm-mock-response.utils'
import { salesMockAdapter } from './sales-mock-adapter'
import {
  createTenant,
  findTenantById,
  listEveryTenant,
  listTenants,
  recordTenantOwnershipChange,
} from './tenants-mock-store'

import type { MockReply } from './sales-crm-mock-response.utils'
import type { CreateTenantInput } from './tenants-mock-store'
import type { CreateProspectRequest, ProspectListItem } from '@/features/sales/types/prospect.types'
import type { BankType, SalesUserRecord, Tenant } from '@/features/sales/types/tenant.types'
import type { AxiosRequestConfig } from 'axios'

/**
 * @description The accepted, unconverted prospects in the caller's cohort, active or archived.
 * @param {SalesUserRecord | undefined} caller - The signed-in caller.
 * @param {boolean} archived - Whether to list archived prospects instead.
 * @returns {Tenant[]} The prospects' tenants.
 */
function acceptedOf(caller: SalesUserRecord | undefined, archived: boolean): Tenant[] {
  return listTenants({ status: 'Prospect', archived }).filter((tenant) =>
    isInCallerCohort(tenant, caller),
  )
}

/**
 * @description The pending submissions the caller may see: their own, or every one for a Super
 * Admin or Sales Head.
 * @param {SalesUserRecord | undefined} caller - The signed-in caller.
 * @returns {Tenant[]} The pending submissions' tenants.
 */
function pendingOf(caller: SalesUserRecord | undefined): Tenant[] {
  if (!caller) return []
  const pending = listTenants({ status: 'Prospect', approval: 'pending' })
  return caller.role === 'Super Admin' || caller.role === 'Sales Head'
    ? pending
    : pending.filter((tenant) => tenant.createdById === caller.id)
}

/**
 * @description The tenants a list request covers, by its status filter: omitted is the accepted
 * prospects plus the visible pending ones, ACTIVE only the accepted, PENDING only the pending,
 * ARCHIVED the dropped ones (Super Admin and Sales Head only) and ALL everything, the dropped last.
 * `backlog=true` keeps only the accepted, unarchived prospects with no open target.
 * @param {SalesUserRecord | undefined} caller - The signed-in caller.
 * @param {Record<string, string | undefined>} query - The request's query parameters.
 * @returns {Tenant[]} The covered tenants (not yet filtered or ordered).
 */
function rowsFor(
  caller: SalesUserRecord | undefined,
  query: Record<string, string | undefined>,
): Tenant[] {
  const isSenior = caller?.role === 'Super Admin' || caller?.role === 'Sales Head'
  const active = acceptedOf(caller, false)
  const pending = pendingOf(caller)
  const archived = isSenior ? acceptedOf(caller, true) : []
  const byStatus: Record<string, Tenant[]> = {
    ACTIVE: active,
    PENDING: pending,
    ARCHIVED: archived,
    ALL: [...active, ...pending, ...archived],
  }
  const rows = byStatus[query.archiveStatus ?? ''] ?? [...active, ...pending]
  if (query.backlog !== 'true') return rows
  const withOpenTarget = new Set(
    listTargets({ status: 'Outstanding' }).map((target) => target.tenantId),
  )
  return active.filter((tenant) => rows.includes(tenant) && !withOpenTarget.has(tenant.id))
}

/** @description The inclusive branch-count range of each band. */
const BRANCH_BANDS: Record<string, [number, number]> = {
  UP_TO_10: [1, 10],
  FROM_11_TO_20: [11, 20],
  FROM_21: [21, Number.POSITIVE_INFINITY],
}

/**
 * @description Whether a row passes the list's search and filters.
 * @param {ProspectListItem} row - The row.
 * @param {Record<string, string | undefined>} query - The request's query parameters.
 * @returns {boolean} True when it matches every one that was sent.
 */
function matchesQuery(row: ProspectListItem, query: Record<string, string | undefined>): boolean {
  const search = query.search?.trim().toLowerCase()
  const checks: [string | undefined, string][] = [
    [query.stateId, row.location.stateId],
    [query.bankTypeId, row.bankType.id],
    [query.tierCode, row.tier.code],
    [query.leadRating, row.leadRating],
  ]
  const searched =
    !search ||
    row.bankName.toLowerCase().includes(search) ||
    row.tenantCode.toLowerCase().includes(search) ||
    row.location.city.toLowerCase().includes(search) ||
    row.location.state.toLowerCase().includes(search)
  const band = BRANCH_BANDS[query.branchBand ?? '']
  const inBand = !band || (row.branchCount >= band[0] && row.branchCount <= band[1])
  return searched && inBand && checks.every(([wanted, actual]) => !wanted || wanted === actual)
}

/**
 * @description Applies the list's search and filters, then orders it.
 * @param {ProspectListItem[]} rows - The cohort's rows.
 * @param {Record<string, string | undefined>} query - The request's query parameters.
 * @returns {ProspectListItem[]} The matching rows, ordered.
 */
function filterAndSort(
  rows: ProspectListItem[],
  query: Record<string, string | undefined>,
): ProspectListItem[] {
  const sortBy = (query.sortBy ?? 'createdAt') as
    'bankName' | 'tenantCode' | 'branchCount' | 'createdAt'
  const descending = (query.sortDirection ?? (sortBy === 'createdAt' ? 'DESC' : 'ASC')) === 'DESC'
  return rows
    .filter((row) => matchesQuery(row, query))
    .sort((a, b) => {
      // Dropped prospects come last under the ALL filter, whatever the order.
      const droppedLast = Number(a.archivedAt !== null) - Number(b.archivedAt !== null)
      const order =
        sortBy === 'branchCount'
          ? a.branchCount - b.branchCount
          : String(a[sortBy]).localeCompare(String(b[sortBy]), undefined, { numeric: true })
      return (
        droppedLast || (descending ? -order : order) || a.prospectId.localeCompare(b.prospectId)
      )
    })
}

/**
 * @description The Sales Executives a caller may name as a new prospect's owner: everyone active
 * for a Super Admin or Sales Head, the active ones in their downline for a Sales Manager.
 * @param {SalesUserRecord} caller - The signed-in caller.
 * @returns {SalesUserRecord[]} The choices, by name.
 */
export function eligibleOwnersFor(caller: SalesUserRecord): SalesUserRecord[] {
  const roster = listSalesRoster()
  const downline =
    caller.role === 'Sales Manager' ? resolveDownlineUserIds(caller.id, roster) : null
  return roster
    .filter(
      (user) =>
        user.role === 'Sales Executive' &&
        user.status === 'Active' &&
        (downline === null || downline.includes(user.id)),
    )
    .sort((a, b) => a.name.localeCompare(b.name))
}

/**
 * @description Checks a Create Prospect body's required fields and references.
 * @param {CreateProspectRequest} body - The request body.
 * @returns {string | null} What's wrong, or null when it's valid.
 */
export function invalidCreateBody(body: CreateProspectRequest): string | null {
  const required = [
    body.bank.name,
    body.bank.registrationNumber,
    body.bank.headOfficeLocation,
    body.address.streetAddress,
    body.address.city,
    body.address.district,
    body.primaryContact.name,
    body.primaryContact.email,
    body.primaryContact.mobileNumber,
  ]
  if (required.some((value) => !value.trim())) return 'A required field is missing.'
  if (!/^[1-9]\d{5}$/.test(body.address.pinCode)) return 'The PIN code is invalid.'
  if (!Number.isInteger(body.organisation.branchCount) || body.organisation.branchCount < 1) {
    return 'The branch count is invalid.'
  }
  const references = [
    catalogueNameById('bankTypes', body.bank.bankTypeId),
    catalogueNameById('states', body.address.stateId),
    catalogueNameById('coreBankingSolutions', body.organisation.coreBankingSolutionId),
    catalogueNameById('leadSources', body.sales.leadSourceId),
  ]
  return references.every(Boolean) ? null : 'A referenced catalogue entry is unknown or inactive.'
}

/**
 * @description Translates a valid Create Prospect body into the Tenant store's create input.
 * @param {CreateProspectRequest} body - The validated request body.
 * @returns {CreateTenantInput} The store's input.
 */
export function toCreateTenantInput(body: CreateProspectRequest): CreateTenantInput {
  const { bank, address, organisation, primaryContact, sales } = body
  return {
    name: bank.name.trim(),
    bankType: catalogueNameById('bankTypes', bank.bankTypeId) as BankType,
    headOffice: bank.headOfficeLocation.trim(),
    regNumber: bank.registrationNumber.trim(),
    state: catalogueNameById('states', address.stateId) ?? '',
    city: address.city.trim(),
    address: address.streetAddress.trim(),
    district: address.district.trim(),
    pincode: address.pinCode,
    website: bank.website ?? '',
    branches: organisation.branchCount,
    employees: organisation.employeeCount ?? null,
    coreBanking:
      catalogueNameById('coreBankingSolutions', organisation.coreBankingSolutionId) ?? '',
    existingMis: organisation.existingMisSolution ?? '',
    authorisedContact: {
      name: primaryContact.name.trim(),
      designation: primaryContact.designation ?? '',
      email: primaryContact.email.trim(),
      phone: primaryContact.mobileNumber,
      officePhone: '',
    },
    leadSource: catalogueNameById('leadSources', sales.leadSourceId) ?? '',
    leadRating: toFrontendRating(sales.leadRating),
    notes: sales.notes ?? '',
  }
}

/**
 * @description Finds what stops a caller's Create Prospect before anything is saved.
 * @param {AxiosRequestConfig} config - The request config.
 * @param {SalesUserRecord} caller - The signed-in caller.
 * @param {CreateProspectRequest} body - The request body.
 * @returns {MockReply | null} The error to answer, or null when it may go ahead.
 */
function createRefusal(
  config: AxiosRequestConfig,
  caller: SalesUserRecord,
  body: CreateProspectRequest,
): MockReply | null {
  if (!headerOf(config, 'Idempotency-Key')) {
    return failReply(428, '0004', 'Idempotency-Key is required')
  }
  const invalid = invalidCreateBody(body)
  if (invalid) return failReply(400, '0001', invalid)
  const registration = body.bank.registrationNumber.trim().toLowerCase()
  if (listEveryTenant().some((tenant) => tenant.regNumber.toLowerCase() === registration)) {
    return failReply(409, '4102', 'A bank with this registration number already exists')
  }
  const proposedOwnerId = body.sales.proposedOwnerUserId
  if (
    caller.role !== 'Sales Executive' &&
    proposedOwnerId &&
    !eligibleOwnersFor(caller).some((user) => user.id === proposedOwnerId)
  ) {
    return failReply(422, '4106', 'The proposed owner is not eligible')
  }
  return null
}

/**
 * @description Creates a prospect the way the backend does: a Sales Executive's waits for review
 * with them as its proposed owner; anyone else's is accepted, owned by the Executive they named.
 * @param {AxiosRequestConfig} config - The request config.
 * @returns {MockReply} The created prospect, or the backend's error.
 */
function createProspectReply(config: AxiosRequestConfig): MockReply {
  const caller = mockCaller()
  if (!caller) return failReply(403, '2008', 'Forbidden')
  const body = bodyOf(config) as unknown as CreateProspectRequest
  const refusal = createRefusal(config, caller, body)
  if (refusal) return refusal

  const isExecutive = caller.role === 'Sales Executive'
  const tenant = createTenant(
    toCreateTenantInput(body),
    isExecutive
      ? {
          approval: 'Pending Approval',
          createdById: caller.id,
          approverId: prospectApproverFor(caller.id, listSalesRoster()),
          requestedOwnerId: caller.id,
        }
      : { approval: 'Approved', createdById: caller.id },
  )
  const proposedOwnerId = body.sales.proposedOwnerUserId
  if (!isExecutive && proposedOwnerId) {
    recordTenantOwnershipChange(tenant.id, {
      fromOwnerId: null,
      toOwnerId: proposedOwnerId,
      actingUserId: caller.id,
      transferredAt: tenant.createdAt,
    })
  }

  const created = {
    prospectId: tenant.id,
    tenantId: tenant.id,
    tenantCode: tenant.id,
    admissionState: isExecutive ? 'PENDING_REVIEW' : 'ACCEPTED',
    approvalRequestId: isExecutive ? `APR-${tenant.id}` : null,
    currentOwner: ownerOf(tenant.currentOwnerId),
    tier: toProspectListItem(tenant).tier,
    submissionRevision: 1,
  }
  return [201, { ...okEnvelope(created), httpStatus: 201 }]
}

/**
 * @description Whether the caller may read a prospect's details: an accepted prospect within
 * their cohort, or a submission they made, are reviewing, or (as Super Admin) any.
 * @param {Tenant} tenant - The prospect.
 * @param {SalesUserRecord | undefined} caller - The signed-in caller.
 * @returns {boolean} True when they may.
 */
export function canReadDetails(tenant: Tenant, caller: SalesUserRecord | undefined): boolean {
  if (!caller) return false
  if (caller.role === 'Super Admin') return true
  if (tenant.approval === undefined || tenant.approval === 'Approved') {
    return isInCallerCohort(tenant, caller)
  }
  return tenant.createdById === caller.id || reviewerOf(tenant) === caller.id
}

/**
 * @description Withdraws the caller's own pending submission the way the backend does.
 * @param {AxiosRequestConfig} config - The request config.
 * @returns {MockReply} The withdrawal, or the backend's error.
 */
function withdrawReply(config: AxiosRequestConfig): MockReply {
  const caller = mockCaller()
  const id = decodeURIComponent(config.url?.split('/').at(-2) ?? '')
  const tenant = findTenantById(id)
  if (!tenant || !canReadDetails(tenant, caller)) {
    return failReply(404, '4101', 'Prospect not found')
  }
  if (!caller || tenant.createdById !== caller.id) return failReply(403, '2008', 'Forbidden')
  if (tenant.approval !== 'Pending Approval') {
    return failReply(409, '4107', 'The prospect is not pending review')
  }
  if (headerOf(config, 'If-Match') !== prospectEtagOf(tenant)) {
    return failReply(412, '4108', 'The prospect changed since it was read')
  }
  tenant.approval = 'Withdrawn'
  recordDecision(tenant.id, {
    decisionId: crypto.randomUUID(),
    decision: 'CANCELLED',
    actorUserId: caller.id,
    reasonCode: 'SUBMITTER_WITHDRAWAL',
    reviewNote: null,
    subjectRevision: 1,
    routingRevision: 1,
    decidedAt: new Date().toISOString(),
  })
  return [
    200,
    okEnvelope({ prospectId: tenant.id, admissionState: 'WITHDRAWN', approvalStatus: 'CANCELLED' }),
  ]
}

/**
 * @description Assigns or reassigns an accepted prospect's owner the way the backend does: a
 * senior only, the prospect visible, accepted and active at the version named, the owner one the
 * caller may assign; naming the current owner changes nothing.
 * @param {AxiosRequestConfig} config - The request config.
 * @returns {MockReply} The owner now and before, or the backend's error.
 */
function assignOwnerReply(config: AxiosRequestConfig): MockReply {
  const caller = mockCaller()
  if (!caller || caller.role === 'Sales Executive') return failReply(403, '2008', 'Forbidden')
  const tenant = findTenantById(decodeURIComponent(config.url?.split('/').at(-2) ?? ''))
  if (!tenant || !canReadDetails(tenant, caller)) {
    return failReply(404, '4101', 'Prospect not found')
  }
  if (admissionStateOf(tenant) !== 'ACCEPTED' || tenant.archived) {
    return failReply(409, '4103', 'The prospect is not accepted and active')
  }
  if (headerOf(config, 'If-Match') !== prospectEtagOf(tenant)) {
    return failReply(412, '4105', 'The prospect changed since it was read')
  }
  const { ownerUserId } = bodyOf(config) as { ownerUserId?: string }
  if (!ownerUserId || !eligibleOwnersFor(caller).some((user) => user.id === ownerUserId)) {
    return failReply(422, '4106', 'The owner is not eligible')
  }
  const previous = tenant.currentOwnerId
  if (previous !== ownerUserId) {
    recordTenantOwnershipChange(tenant.id, {
      fromOwnerId: previous,
      toOwnerId: ownerUserId,
      actingUserId: caller.id,
      transferredAt: new Date().toISOString(),
    })
  }
  const assignment = {
    prospectId: tenant.id,
    tenantId: tenant.id,
    tenantCode: tenant.id,
    currentOwner: ownerOf(ownerUserId),
    previousOwnerUserId: previous,
  }
  return [200, okEnvelope(assignment)]
}

if (salesMockAdapter) {
  salesMockAdapter
    .onGet('/tenants/prospect-metadata')
    .reply(() => [200, okEnvelope(mockProspectMetadata())])

  salesMockAdapter.onGet('/sales/prospects/summary').reply(() => {
    // The KPIs count the accepted, unarchived prospects in the caller's cohort (targeted ones
    // included) and every pending submission in the organisation, the same for every role.
    const cohort = acceptedOf(mockCaller(), false)
    const pending = listTenants({ status: 'Prospect', approval: 'pending' }).length
    return [
      200,
      okEnvelope({
        totalProspects: cohort.length,
        totalBranches: cohort.reduce((sum, tenant) => sum + tenant.branches, 0),
        hotLeads: cohort.filter((tenant) => tenant.leadRating === 'Hot').length,
        pendingApproval: pending,
        evaluatedAt: new Date().toISOString(),
      }),
    ]
  })

  salesMockAdapter.onGet('/sales/prospects/eligible-owners').reply((config) => {
    const caller = mockCaller()
    if (!caller || caller.role === 'Sales Executive') return failReply(403, '2008', 'Forbidden')
    const search = queryOf(config).search?.trim().toLowerCase()
    const choices = eligibleOwnersFor(caller)
      .filter(
        (user) =>
          !search ||
          user.name.toLowerCase().includes(search) ||
          user.id.toLowerCase().includes(search),
      )
      .map((user) => ({
        userId: user.id,
        displayName: user.name,
        employeeCode: user.id,
        positionCode: 'SALES_EXECUTIVE',
      }))
    return pageReply(config, choices)
  })

  salesMockAdapter.onGet('/sales/prospects').reply((config) => {
    const query = queryOf(config)
    const rows = rowsFor(mockCaller(), query).map(toProspectListItem)
    return pageReply(config, filterAndSort(rows, query))
  })

  salesMockAdapter.onGet(/^\/sales\/prospects\/[^/]+$/).reply((config) => {
    const id = decodeURIComponent(config.url?.split('/').at(-1) ?? '')
    const tenant = findTenantById(id)
    if (!tenant || tenant.status !== 'Prospect' || !canReadDetails(tenant, mockCaller())) {
      return failReply(404, '4101', 'Prospect not found')
    }
    return [200, okEnvelope(toProspectDetails(tenant)), { etag: prospectEtagOf(tenant) }]
  })

  salesMockAdapter.onPost('/sales/prospects').reply(createProspectReply)

  salesMockAdapter.onPost(/^\/sales\/prospects\/[^/]+\/withdraw$/).reply(withdrawReply)

  salesMockAdapter.onPost(/^\/sales\/prospects\/[^/]+\/assign-owner$/).reply(assignOwnerReply)
}
